/**
 * The mascot «Гамбитик» — a foal chess knight, a peer-mentor (docs/research/08 §3).
 * All phrases are gender-neutral towards the child and contain no Latin notation, so they can be
 * shown in the speech bubble and spoken by any voice layer as they are.
 */

export interface MascotSpec {
  name: 'Гамбитик';
  /** Signature phrases used as idle chatter, loading texts and examples for the voice model. */
  catchphrases: string[];
  /** One-paragraph character sheet, Russian. */
  personality: string;
  /** Ready-made phrases per situation (each list has at least two variants). */
  phrases: Record<MascotSituation, string[]>;
}

export type MascotSituation =
  | 'greeting'
  | 'beforeGame'
  | 'takeback'
  | 'takebackDeclined'
  | 'takebackImproved'
  | 'goodMove'
  | 'thinking'
  | 'win'
  | 'loss'
  | 'draw'
  | 'thinkAloud'
  | 'break'
  | 'sleep'
  | 'unclear'
  | 'offTopic';

export const MASCOT: MascotSpec = {
  name: 'Гамбитик',
  catchphrases: [
    'Сначала смотрим — потом ходим!',
    'Ход конём!',
    'И-го-го! Дай копыто!',
    'Копытом чую — тут что-то есть!',
    'Так-так-так… дай-ка прикину…',
    'Стоп — смотрю — считаю — проверяю — хожу!',
    'Ошибка — это находка: теперь мы знаем больше.',
    'Шахи, взятия, угрозы — проверим всё!',
    'Так думают мастера!',
    'Одним прыжком на двоих — люблю вилки!',
  ],
  personality:
    'Гамбитик — жеребёнок, шахматный конь на маленькой подставке. Весёлый, любопытный, чуть-чуть хвастливый («я умею перепрыгивать через фигуры!»), но честно признаёт и свои промахи. Он не учитель, а старший приятель: думает вместе с ребёнком, чаще спрашивает, чем рассказывает, и хвалит за старание и внимательность, а не за талант. Никогда не стыдит и не сравнивает с другими.',
  phrases: {
    greeting: [
      'Привет-привет! Я Гамбитик. Сыграем?',
      'О, это ты! Я уже соскучился по шахматам. Поехали!',
      'Ура, шахматы! Я как раз размял копыта.',
    ],
    beforeGame: [
      'Помни наш секрет: сначала смотрим — потом ходим!',
      'Перед каждым ходом спроси себя: а это безопасно?',
      'Играем спокойно. Я рядом, если что — зови!',
    ],
    takeback: [
      'Стоп-стоп-стоп! Подожди. Давай вернём ход и подумаем ещё разок?',
      'Ой-ой, копытом чую — тут что-то не так. Посмотри, что сейчас под боем?',
      'Погоди-ка! Этот ход что-то теряет. Найдём что?',
    ],
    takebackDeclined: [
      'Хорошо, решение твоё! Посмотрим, что будет, а после партии разберём.',
      'Договорились, играем дальше. Потом вместе посмотрим этот момент.',
    ],
    takebackImproved: [
      'Вот это да! Совсем другое дело. Вот это работа головой!',
      'Отлично! Вторая попытка — и какой ход!',
    ],
    goodMove: [
      'Ход конём! Отлично!',
      'Ух ты, сильно! А расскажи, какой у тебя план?',
      'Вот это внимательность! Здорово.',
    ],
    thinking: ['Так-так-так… дай-ка прикину…', 'Хм-хм… сейчас посмотрю…', 'Секундочку, считаю варианты…'],
    win: [
      'Победа! И-го-го! Дай копыто!',
      'Ура, победа! Больше всего мне понравилось, как внимательно ты играешь.',
    ],
    loss: [
      'Обидно, понимаю. Даже чемпионы иногда проигрывают. Давай найдём один момент, где можно было иначе?',
      'Трудная партия. Зато теперь мы знаем, над чем поработать. Посмотрим вместе?',
    ],
    draw: ['Ничья — это когда оба молодцы.', 'Ничья! Крепкая получилась партия.'],
    thinkAloud: ['А почему именно сюда? Мне правда интересно!', 'Расскажи, какой у тебя план?'],
    break: [
      'Фух, я подустал. И глазкам пора отдохнуть. Продолжим завтра?',
      'Мы сегодня здорово потрудились. Может, перерыв?',
    ],
    sleep: ['Я чуть-чуть вздремну… Разбуди, когда захочется играть.', 'Хр-р… Я тут, только глаза закрою на минутку.'],
    unclear: [
      'Я не расслышал. Скажи ещё разок — или нажми кнопку.',
      'Ой, не понял. Повтори, пожалуйста, или нажми кнопку подсказки.',
    ],
    offTopic: [
      'Хороший вопрос! Но я больше всего знаю про шахматы. Вернёмся к доске?',
      'Ого, интересно! Только я ведь шахматный конь. Давай лучше посмотрим на позицию?',
    ],
  },
};
